import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Row } from 'components/simpleFlexbox';
import { createUseStyles, useTheme } from 'react-jss';
import queryString from 'query-string';
import { validateEmail } from 'resources/utilities';
import ButtonComponent from 'components/form/ButtonComponent';
import FormComponent from 'components/form/FormComponent';
import FormInputComponent from 'components/form/input';
import FormCheckboxComponent from 'components/form/checkbox/FormCheckboxComponent';
import { IconShowPassword, IconHidePassword } from 'assets/icons';

const useStyles = createUseStyles((theme) => ({
    separator: {
        backgroundColor: theme.color.pink,
        width: '100%',
        height: 2,
        margin: '20px 0px'
    }
}));

function CreateAccountComponent({
    errorMessage,
    onSubmit,
    title = 'Sign up free',
    showPhoneInput = true
}) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const { search } = useLocation();
    const { provider } = queryString.parse(search);

    const [passwordShown, setPasswordShown] = useState(false);

    const togglePassword = (e) => {
        e.preventDefault();
        setPasswordShown(!passwordShown);
    };

    function onLocalSubmit(payload) {
        try {
            window.gtag('event', 'conversion', {
                send_to: 'AW-10974718715/GKVbCMWxit8DEPvVk_Eo',
                event_callback: () => {}
            });
        } catch (ex) {}
        try {
            window.lintrk('track', { conversion_id: 9853284 });
        } catch (ex) {}
        onSubmit(payload);
    }

    function onSignupFederatedClick() {
        Auth.federatedSignIn({ customProvider: provider });
    }

    return (
        <>
            <h1>{title}</h1>
            {errorMessage && <span className='signup-error-message'>{errorMessage}</span>}
            <FormComponent onSubmit={onLocalSubmit}>
                <Row horizontal='space-between' className='signup-input-row'>
                    <FormInputComponent
                        label='First Name'
                        name='firstName'
                        required='Please provide a valid first name'
                        autoComplete='off'
                        showErrorMessageBelow
                    />
                    <FormInputComponent
                        label='Last Name'
                        name='lastName'
                        required='Please provide a valid last name'
                        autoComplete='off'
                        showErrorMessageBelow
                    />
                </Row>
                <Row className='signup-input-full-width'>
                    <FormInputComponent
                        type='email'
                        label='Work Email'
                        name='email'
                        required='Please provide a valid email address'
                        validate={(value) => validateEmail(value)}
                        pattern={{
                            value: /^[A-Z0-9._%+-/']+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Please provide a valid email address'
                        }}
                        autoComplete='off'
                        showErrorMessageBelow
                    />
                </Row>
                {showPhoneInput && (
                    <Row className='signup-input-full-width'>
                        <FormInputComponent
                            label='Phone Number'
                            name='phone'
                            type='text'
                            minLength={{
                                value: 8,
                                message: 'Please provide a valid phone number'
                            }}
                            required='Please provide a valid phone number'
                            autoComplete='off'
                            showErrorMessageBelow
                        />
                    </Row>
                )}
                <Row
                    className='signup-input-full-width'
                    style={{ justifyContent: 'space-between', position: 'relative' }}
                >
                    <div>
                        <FormInputComponent
                            label='Password'
                            name='password'
                            type={passwordShown ? 'text' : 'password'}
                            required='Passwords must be at least 8 characters long and contain at least 1 lowercase letter, 1 uppercase letter, 1 number and one special character'
                            minLength={{
                                value: 8,
                                message:
                                    'Passwords must be at least 8 characters long and contain at least 1 lowercase letter, 1 uppercase letter, 1 number and one special character'
                            }}
                            pattern={{
                                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?\-“!@#%&/,><’:;|_~`])\S{6,99}$/,
                                message:
                                    'Passwords must be at least 8 characters long and contain at least 1 lowercase letter, 1 uppercase letter, 1 number and one special character'
                            }}
                            autoComplete='off'
                            showErrorMessageBelow
                        />
                    </div>
                    <span className='signup-show-hide-btn' onClick={(e) => togglePassword(e)}>
                        {passwordShown ? (
                            <IconShowPassword height={40} width={40} />
                        ) : (
                            <IconHidePassword height={40} width={40} />
                        )}
                    </span>
                </Row>
                <Row vertical='center' className='signup-buttons-container'>
                    <ButtonComponent id='create_account' type='submit' className='signup-button'>
                        Create Account
                    </ButtonComponent>
                </Row>
                <Row className='signup-terms'>
                    <FormCheckboxComponent
                        name='agree'
                        required='You must agree with the terms & conditions and privacy policy before creating an account'
                        noSelectableLabel
                        label={
                            <div className='flex whitespace-nowrap gap-1'>
                                I agree to the Dataro
                                <a
                                    href='https://dataro.io/privacy-policy-2/'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='underline underline-offset-2 hover:text-purple-headers transition-all'
                                >
                                    Privacy Policy
                                </a>
                                and
                                <a
                                    href='http://dataro.io/terms-conditions/'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='underline underline-offset-2 hover:text-purple-headers transition-all'
                                >
                                    Terms & Conditions
                                </a>
                                *
                            </div>
                        }
                    />
                </Row>
            </FormComponent>
            {provider && (
                <>
                    <div className={classes.separator}></div>
                    <ButtonComponent
                        id='create_federated_account'
                        className='signup-button'
                        onClick={onSignupFederatedClick}
                    >
                        Federated Sign In
                    </ButtonComponent>
                </>
            )}
        </>
    );
}

export default CreateAccountComponent;
